import { DateTime } from "luxon";
import { AxiosResponse } from "axios";
import { DEFAULT_FILE_TYPE, NotificationStatus } from "@/global/data.ts";
import { FormattedDate, SortDirection } from "@/global/types.ts";
import { LocationQuery } from "vue-router";
import { User } from "oidc-client-ts";
import i18n from "@/i18n";
import { DownloadReportResponse, UploadReportResponse } from "./interfaces";
import { IResponse } from "@/api";
import { FEATURE_NAMES, logError } from "@/loggers";

// begin - date utilities
const getFutureDate = (duration: Record<string, any>) => {
  const futureDate = DateTime.now().plus(duration);
  return futureDate
    .toUTC()
    .toISO({ suppressMilliseconds: true })
    .replace(/\.\d{0,3}/, "");
};

const getTodayDate = () =>
  DateTime.now()
    .toUTC()
    .toISO({ suppressMilliseconds: true })
    .replace(/\.\d{0,3}/, "");

/** Formats a Date as `YYYY-MM-DD` in the local timezone. */
export const formatDateYMD = (date: Date): string => {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${mm}-${dd}`;
};

/** Today as `YYYY-MM-DD` in the local timezone. */
export const getTodayYMD = (): string => formatDateYMD(new Date());

const formatDateTime = (
  isoString: string,
  timeZone?: string,
  format: string = "dd LLL yyyy HH:mm",
  locale: string = "en"
): FormattedDate => {
  let dateTime = DateTime.fromISO(isoString, { setZone: true });
  if (timeZone) {
    dateTime = dateTime.setZone(timeZone);
  }
  return {
    formattedDate: dateTime.toFormat(format, { locale }),
    timeZoneName: dateTime.offsetNameShort
  };
};
// end - date utilities

// begin - general logic
const decodeJwt = (token: string) => {
  if (!token) return null;
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

const debounce = (func: (...args: any[]) => void, wait: number = 500) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function (...args: any[]) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

const setAppQueryParams = (params: LocationQuery, queryParams?: LocationQuery) => {
  return {
    ...params,
    ...queryParams
  };
};
const excludeAppQueryParams = (
  params: LocationQuery | Record<string, any>,
  excludedQueryParams: Array<keyof LocationQuery> = ["tab" as keyof LocationQuery]
) => {
  const finalQueryParams = {};

  for (const [key, value] of Object.entries(params)) {
    if (excludedQueryParams.includes(key)) continue;
    finalQueryParams[key] = value;
  }

  return finalQueryParams;
};

const toCamelCase = (str: string): string =>
  str
    .toLowerCase()
    .replace(/\s(.)/g, (match) => match.toUpperCase())
    .replace(/\s+/g, "");
// end - general logic

// begin - download logic

const downloadFileInBrowser = (
  response: AxiosResponse | IResponse,
  fileName: string,
  fileType: string = DEFAULT_FILE_TYPE
) => {
  const url = URL.createObjectURL(new Blob([response.data], { type: fileType }));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
// end - download logic

export const formatNumber = (number: number) => {
  return number.toLocaleString("en-US", { maximumFractionDigits: 2, notation: "compact", compactDisplay: "short" });
};
export const formatNumberToIn = (number: number) => {
  return number.toLocaleString("en-IN");
};

export const roundToDecimal = (num: number, digits: number) => {
  const factor = Math.pow(10, digits);
  return Math.round(num * factor) / factor;
};

export const normalisedText = (text: string) => {
  return typeof text === "string"
    ? text?.replace(/_/g, " ").charAt(0).toUpperCase() + text?.replace(/_/g, " ").slice(1).toLowerCase()
    : "";
};

export const capitalizeFirstLowerRest = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Joins a city and a country into "City, Country", tolerating either side being missing.
 * @param {string} [city] - The city name.
 * @param {string} [country] - The country name.
 * @returns {string | undefined} The joined value, or undefined when both are empty so the cell renders blank.
 */
export const formatCityCountry = (city?: string, country?: string): string | undefined => {
  const parts = [city, country].map((part) => part?.toString().trim()).filter(Boolean);
  return parts.length ? parts.join(", ") : undefined;
};

// ------- aggregating global module exports -------
const downloadLogic = {
  downloadFileInBrowser
};

const dateUtilities = {
  getFutureDate,
  getTodayDate
};

const generalLogic = {
  decodeJwt,
  debounce,
  excludeAppQueryParams,
  setAppQueryParams,
  formatDateTime,
  toCamelCase,
  capitalizeFirstLowerRest
};

const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === "true";

const mockUserToken = {
  sub: "mock-user-id",
  email: import.meta.env.VITE_MOCK_USER_EMAIL || "test@maersk.com",
  name: import.meta.env.VITE_MOCK_USER_NAME || "Test User",
  roles: [
    "iscednd.read",
    "iscecongestion.read",
    "ISCE.read",
    "ISCE.write",
    "isceemission.write",
    "iscebks.read",
    "isce.admin",
    "MaerskInternal"
  ],
  customer_code: "MAEU"
};

const createMockUser = (): User => {
  return {
    userToken: mockUserToken,
    roles: mockUserToken.roles,
    profile: {
      firstname: "Test",
      lastname: "User",
      email: mockUserToken.email
    },
    preferences: {
      termsAccepted: true
    },
    customer: {
      id: 1,
      tenantId: "MAEU",
      customerCode: "MAEU",
      customerName: "Maersk Line",
      uiEnabled: true,
      dndEnabled: true,
      disruptionEnabled: true,
      dndExportEnabled: true,
      airEnabled: true,
      emissionEnabled: true,
      mrmEnabled: true,
      // Local/mock only: enable Booking & Volume Performance nav + /bookings route
      bksEnabled: true,
      features: {
        subscription_flexi_column_enabled: true,
        enable_async_upload: true,
        enable_settlement_currency: true,
        shipment_user_column_enabled: true,
        metering_list_item_table_enabled: true,
        control_tower_enabled: true,
        vite_async_reporting: true,
        container_type_conversion_enabled: true,
        carrier_performance_enabled: true,
        uam_enabled: true,
        mrm_disruption_filters_enabled: true,
        mrm_url_filter_mapping_enabled: true,
        date_type_standardisation_enabled: true,
        enable_captain_peter: true,
        shipment_id_cs_column_enabled: true,
        po_risk_enabled: true
      }
    }
  };
};

/**
 * A circuit breaker function to handle retries and cooldowns for asynchronous operations.
 *
 * @template T - The type of the value returned by the asynchronous function.
 * @param {() => Promise<T>} fn - The asynchronous function to execute.
 * @param {number} [maxRetries=3] - The maximum number of retry attempts before cooldown.
 * @param {number} [retryDelayMs=3000] - The delay (in milliseconds) between retry attempts.
 * @param {number} [cooldownMs=3600000] - The cooldown period (in milliseconds) after all retries fail.
 * @param {{ count: number }} [attemptTracker] - An optional tracker to count the total number of attempts.
 * @param {number} [totalRetryLimit] - An optional limit for the total number of retries across all calls.
 * @returns {Promise<T | null>} - Resolves with the result of the function or null if all retries fail.
 */
const circuitBreaker = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  retryDelayMs: number = 3000,
  cooldownMs: number = 3600000,
  attemptTracker?: { count: number }, // optional
  totalRetryLimit?: number // optional
): Promise<T | null> => {
  let attempts = 0;

  if (!attemptTracker) attemptTracker = { count: 0 };

  while (attempts < maxRetries) {
    try {
      attemptTracker.count++;
      if (totalRetryLimit !== undefined && attemptTracker.count > totalRetryLimit) {
        console.warn("Retry limit exceeded (in test).");
        return null;
      }

      return await fn();
    } catch (error) {
      attempts++;
      console.warn(`Attempt ${attempts} failed. Retrying in ${retryDelayMs / 1000} seconds...`, error);

      if (attempts < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      }
    }
  }

  console.warn(`Cooling down for ${cooldownMs / 60000} minutes...`);
  await new Promise((resolve) => setTimeout(resolve, cooldownMs));

  return circuitBreaker(fn, maxRetries, retryDelayMs, cooldownMs, attemptTracker, totalRetryLimit);
};

/**
 * Returns the time left until expiry (calculated from now until expiresAt)
 * @param expiresAt - The expiry time as a string, number, or Date
 * @returns {hours: n} or {minutes: n} or 'already expired' or 'NA'
 */
export type TimeLeft = { hours?: number; minutes?: number };
const getHoursLeftToExpire = (expiresAt: string | number | Date): TimeLeft | string => {
  let expiryDate: Date;
  if (typeof expiresAt === "string" || typeof expiresAt === "number") {
    expiryDate = new Date(expiresAt);
  } else if (expiresAt instanceof Date) {
    expiryDate = expiresAt;
  } else {
    return i18n.global.t("NA");
  }

  if (isNaN(expiryDate.getTime())) {
    return i18n.global.t("NA");
  }

  const msLeft = expiryDate.getTime() - Date.now();

  if (msLeft <= 0) {
    return i18n.global.t("NOTIFICATION.EXPIRED");
  }

  const minLeft = msLeft / (1000 * 60);
  if (minLeft < 60) {
    return { minutes: Math.ceil(minLeft) };
  }

  const hourLeft = msLeft / (1000 * 60 * 60);

  return { hours: Math.ceil(hourLeft) };
};

const rtlClass = { "rtl-direction": i18n.global.locale.value === "ar" };

export const isNotificationInProgress = (notification: DownloadReportResponse | UploadReportResponse): boolean => {
  return notification.status !== NotificationStatus.IN_PROGRESS;
};
export const isNotificationSuccessful = (notification: DownloadReportResponse | UploadReportResponse): boolean => {
  return notification.status === NotificationStatus.SUCCESSFUL;
};

/**
 * Creates a deep copy of the given value.
 * @param value - The value to copy.
 * @returns A deep copy of the value.
 */
const getDeepCopyObject = (value: any): any => {
  if (!Array.isArray(value)) return value;

  const finalValue = [];

  if (value.length > 0) {
    value.forEach((item) => {
      if (item && typeof item === "object") {
        finalValue.push({ ...item });
      } else {
        finalValue.push(item);
      }
    });
  }

  return finalValue;
};

const getFormattedDirection = (sortDir?: SortDirection): string => {
  if (!sortDir) return "ascending"; // default
  return sortDir === SortDirection.ASC ? "ascending" : "descending";
};

/**
 * Automatically downloads a file from the given URL.
 * @param {string} url - The URL of the file to download.
 * @param {string} fileName - The name to use for the downloaded file.
 * @param {boolean} isDynamicTemplate - Whether to use the API template download endpoint.
 */
const autoDownload = async (url: string, fileName: string, isDynamicTemplate: boolean = false) => {
  try {
    if (isDynamicTemplate) {
      // Dynamic import to avoid side effects during module initialization
      const { downloadDynamicTemplate } = await import("./downloadHelpers");
      await downloadDynamicTemplate(url, fileName);
    } else {
      // Dynamic import to avoid side effects during module initialization
      const { downloadViaFetch } = await import("./downloadHelpers");
      downloadViaFetch(url, fileName);
    }
  } catch (err) {
    logError(
      FEATURE_NAMES.MVS_TEMPLATE_DOWNLOAD,
      `Auto download failed for ${fileName} from ${url}: ${err instanceof Error ? err.message : String(err)}`
    );
    throw err;
  }
};

/**
 * Splits comma-separated string values in a payload object into arrays for the specified keys.
 * Useful when a filter store returns joined strings but the API requires repeated query params.
 * @param payload - The source payload object.
 * @param keys - The keys whose values should be split.
 * @returns A new object with the specified keys expanded to string arrays.
 */
const splitCommaValuesToArray = <T extends Record<string, any>>(payload: T, keys: string[]): T => {
  const result = { ...payload };
  for (const key of keys) {
    if (typeof result[key] === "string" && result[key]) {
      (result as any)[key] = (result[key] as string).split(",");
    }
  }
  return result;
};

/**
 * Trim a number decimal - if it has decimals, returns toFixed(2), else returns whole number.
 * @param {number} value - The number to format.
 * @returns {number} The formatted number.
 */
const trimDecimal = (value: number, decimals: number = 2): number => {
  if (!Number.isInteger(value)) {
    return parseFloat(value?.toFixed(decimals));
  }
  return value;
};

export {
  downloadLogic,
  dateUtilities,
  generalLogic,
  debounce,
  createMockUser,
  bypassAuth,
  circuitBreaker,
  rtlClass,
  getHoursLeftToExpire,
  getDeepCopyObject,
  getFormattedDirection,
  autoDownload,
  trimDecimal,
  splitCommaValuesToArray
};
